import React, { useCallback, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "../common/Navbar";
import MediaCard from "./MediaCard";
import BrandTile from "./BrandTile";
import WorkIntro from "./WorkIntro";
import { works } from "../../data/works";
import { buildCollage } from "./collageLayout";
import "../../styles/ourWorks.css";

gsap.registerPlugin(ScrollTrigger);

const collage = buildCollage(works);

const OurWorks = () => {
  const [introDone, setIntroDone] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const rootRef = useRef(null);

  const handleIntroDone = useCallback(() => setIntroDone(true), []);
  const closeExpanded = useCallback(() => setExpanded(null), []);

  // Esc closes the lightbox, same as clicking off it.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeExpanded();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, closeExpanded]);

  // Lock the page while the title sequence runs.
  useEffect(() => {
    if (introDone) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [introDone]);

  // The collage is a normal, already-composed page — every tile is visible
  // immediately on load, brand tile included, instead of staying blank until
  // scrolled into view. The only motion left is a continuous, subtle parallax
  // drift on the media as the page scrolls past it.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-tile]").forEach((tile) => {
        const media = tile.querySelector("[data-parallax]");
        if (!media) return;

        gsap.fromTo(
          media,
          { yPercent: -7 },
          {
            yPercent: 7,
            ease: "none",
            scrollTrigger: {
              trigger: tile,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          }
        );
      });
    }, root);

    const refresh = setTimeout(() => ScrollTrigger.refresh(), 260);
    return () => {
      clearTimeout(refresh);
      ctx.revert();
    };
  }, []);

  return (
    <div className={`ow ${introDone ? "is-revealed" : ""}`} ref={rootRef}>
      {!introDone && <WorkIntro onDone={handleIntroDone} />}

      <Navbar />

      <div className="ow-grid">
        {collage.map((item, i) =>
          item.kind === "brand" ? (
            <BrandTile
              key={item.key}
              col={item.col}
              rowStart={item.rowStart}
              rowSpan={item.rowSpan}
            />
          ) : (
            <MediaCard
              key={item.key}
              work={item.work}
              index={i}
              col={item.col}
              rowStart={item.rowStart}
              rowSpan={item.rowSpan}
              onExpand={setExpanded}
            />
          )
        )}
      </div>

      {expanded && (
        <div className="ow-lightbox" onClick={closeExpanded}>
          <div className="ow-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="ow-lightbox-close"
              type="button"
              aria-label="Close"
              onClick={closeExpanded}
            >
              ×
            </button>
            <video
              key={expanded.id}
              src={expanded.media.src}
              poster={expanded.media.poster || undefined}
              autoPlay
              controls
              playsInline
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OurWorks;
